import React, { memo, useEffect, useState } from 'react';
import type { RichTextEntryType } from 'tsconfig/types.d';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { fontCorrectionInRichText } from '../../../utils/fontCorrectionInRichText';
import RichText from '../../RichText/RichText';
import { contentToSanitize, parseHtmlContent } from '../../../utils/richTextHelper';

export type CmsRichTextProps = {
  contentItem: RichTextEntryType;
  dlClickEventHandler?: (data: string) => void;
};

export const CmsRichText = memo((props: CmsRichTextProps) => {
  const { contentItem, dlClickEventHandler } = props;
  const theme = useTheme();
  const widgetTheme = theme.widgets.CmsRichText || {};
  const Wrapper = styled.section`
    ${contentItem?.fields?.backgroundColour && ` background-color: ${contentItem?.fields?.backgroundColour}`}
    ${widgetTheme.wrapper}
  `;

  const [modifiedContent, setModifiedContent] = useState('');

  const handleClickEvent = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');

    if (!anchor) {
      return;
    }

    // Clone the anchor so we don't modify the original
    const clone = target.cloneNode(true) as Element;

    // Remove all elements that are hidden with display: none
    clone.querySelectorAll('*').forEach((el: Element) => {
      const inlineStyle = (el as HTMLElement).style.display;
      if (inlineStyle === 'none') {
        el.remove();
      }
    });

    // Get only the visible text content
    const visibleText = clone.textContent?.trim();
    visibleText && dlClickEventHandler?.(visibleText || '');
  };

  useEffect(() => {
    const updatedContent = parseHtmlContent(fontCorrectionInRichText(contentToSanitize(contentItem), theme.fonts));
    setModifiedContent(updatedContent);
  }, [contentItem]);

  //see https://www.contentful.com/developers/docs/concepts/rich-text/
  return (
    <Wrapper
      data-test="contentful-textbox"
      className={`richtext-wrapper ${contentItem?.fields?.backgroundColour ? 'richtext-has-bg' : ''}`}
      onClick={handleClickEvent}
    >
      {typeof contentItem?.fields?.content === 'string' && (
        <RichText sysId={contentItem.sys.id} content={modifiedContent} />
      )}
    </Wrapper>
  );
});
